import { Bell } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { UserWorkspace } from '@/api/types'
import { useSessionStore } from '@/features/auth/session-store'
import { useTenantWorkspace } from '@/features/workspace/use-tenant-workspace'
import { Button } from '@/shared/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { unreadHere } from '../counts'
import { NotificationList } from './notification-list'

/**
 * The bell, and everything behind it.
 *
 * Notifications are per workspace on the server, and there is one workspace, so
 * the count on the bell is the whole account's.
 */
export function NotificationPanel() {
  const { t } = useTranslation()
  const { workspace } = useTenantWorkspace()
  const userUnreadCount = useSessionStore((state) => state.unreadNotificationCount)

  const here = unreadHere(workspace, userUnreadCount)

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={
            here > 0
              ? t('notification.openUnread', { count: here })
              : t('notification.open')
          }
        >
          <Bell />
          {here > 0 && (
            <span className="absolute top-0.5 right-0.5 min-w-4 rounded-full bg-primary px-1 text-[10px] leading-4 font-medium text-primary-foreground">
              {here > 99 ? '99+' : here}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      {/* Opens up and to the right: the bell sits in the sidebar's footer. */}
      <PopoverContent side="top" align="start" className="w-96 p-0">
        {workspace === undefined ? (
          <p className="p-4 text-sm text-muted-foreground">
            {t('notification.noWorkspace')}
          </p>
        ) : (
          <Panel workspace={workspace} />
        )}
      </PopoverContent>
    </Popover>
  )
}

function Panel({ workspace }: { workspace: UserWorkspace }) {
  const { t } = useTranslation()

  return (
    <>
      <header className="flex items-center justify-between border-b px-4 py-2">
        <h2 className="text-sm font-medium">{t('notification.title')}</h2>
        <span className="truncate text-xs text-muted-foreground">{workspace.name}</span>
      </header>
      {/* Keyed so switching workspaces starts a new list rather than paging on
          into the previous one's offsets. */}
      <NotificationList key={workspace.id} workspaceId={workspace.id} />
    </>
  )
}
